/**
 * MeasurementGuideModal.jsx — Guide précis de prise des mensurations : explique où
 * positionner le mètre ruban pour chaque zone mesurable dans le formulaire, + règles
 * générales pour des mesures fiables et comparables dans le temps.
 */
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Ruler, ChevronsLeftRight, CircleDot, Info, CheckCircle2,
} from 'lucide-react';

const STEPS = [
  {
    key: 'chest', fr: 'Poitrine', en: 'Chest',
    fr_d: "Passez le mètre autour de la poitrine, au niveau des tétons, bras relâchés le long du corps. Respirez normalement, ne bloquez pas votre respiration.",
    en_d: 'Wrap the tape around your chest, at nipple level, arms relaxed by your sides. Breathe normally, do not hold your breath.',
  },
  {
    key: 'waist', fr: 'Taille', en: 'Waist',
    fr_d: "Mesurez au niveau naturel de la taille (juste au-dessus du nombril), mètre bien horizontal. Ne rentrez pas le ventre.",
    en_d: 'Measure at the natural waist level (just above the navel), tape perfectly horizontal. Do not suck in your stomach.',
  },
  {
    key: 'hips', fr: 'Hanches', en: 'Hips',
    fr_d: "Mesurez au point le plus large des hanches, généralement au niveau du pubis, pieds joints.",
    en_d: 'Measure at the widest point of your hips, usually at pubic bone level, feet together.',
  },
  {
    key: 'biceps', fr: 'Biceps', en: 'Biceps',
    fr_d: "Bras relâché le long du corps, mesurez à mi-hauteur du biceps (à distance égale entre l'épaule et le coude). Un seul bras suffit, les 2 étant globalement symétriques.",
    en_d: 'Arm relaxed by your side, measure at mid-bicep height (equal distance between shoulder and elbow). One arm is enough, both are roughly symmetrical.',
  },
  {
    key: 'thigh', fr: 'Cuisse', en: 'Thigh',
    fr_d: "Debout, jambe légèrement écartée, mesurez juste sous le pli fessier, au point le plus large de la cuisse.",
    en_d: 'Standing, leg slightly apart, measure just below the glute fold, at the widest point of the thigh.',
  },
  {
    key: 'calf', fr: 'Mollet', en: 'Calf',
    fr_d: "Debout, poids réparti sur les 2 jambes, mesurez au point le plus large du mollet.",
    en_d: 'Standing, weight evenly distributed, measure at the widest point of the calf.',
  },
];

const RULES = [
  { fr: 'Toujours au même moment de la journée, idéalement le matin à jeun.', en: 'Always at the same time of day, ideally in the morning on an empty stomach.' },
  { fr: 'Toujours dans les mêmes conditions (avant le repas, sans vêtements épais).', en: 'Always in the same conditions (before eating, without thick clothing).' },
  { fr: 'Mètre ruban souple : ni trop serré (marque la peau), ni trop lâche.', en: 'Flexible tape measure: not too tight (marks the skin), not too loose.' },
  { fr: 'Idéalement, faites-vous aider par quelqu\'un pour plus de précision.', en: 'Ideally, get someone to help you for more precision.' },
  { fr: 'Mesurez 1x par semaine ou toutes les 2 semaines, jamais tous les jours.', en: 'Measure once a week or every 2 weeks, never every day.' },
];

export const MeasurementGuideModal = ({ open, onClose, en }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
  <AnimatePresence>
    {open && (
      <motion.div
        className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="measurement-guide-modal"
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative w-full sm:max-w-2xl bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Ruler className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-bold text-white">{en ? 'How to take your measurements' : 'Comment prendre vos mensurations'}</h3>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="measurement-guide-close"><X className="w-5 h-5" /></button>
          </div>

          <p className="text-xs font-bold tracking-wider text-primary mb-3">{en ? 'ZONE BY ZONE' : 'ZONE PAR ZONE'}</p>
          <div className="space-y-3 mb-6">
            {STEPS.map((s) => (
              <div key={s.key} className="rounded-2xl bg-zinc-800/50 border border-white/10 p-4 flex items-start gap-3" data-testid={`guide-zone-${s.key}`}>
                <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center shrink-0 mt-0.5">
                  <CircleDot className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-white text-sm mb-1">{en ? s.en : s.fr}</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">{en ? s.en_d : s.fr_d}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-primary/10 to-zinc-900 border border-white/10 p-5" data-testid="guide-general-rules">
            <div className="flex items-center gap-2 mb-3">
              <ChevronsLeftRight className="w-5 h-5 text-primary" />
              <h4 className="font-bold text-white">{en ? 'Golden rules for reliable measurements' : 'Règles d\'or pour des mesures fiables'}</h4>
            </div>
            <div className="space-y-2">
              {RULES.map((r, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-zinc-300">{en ? r.en : r.fr}</p>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-2.5 mt-3 pt-3 border-t border-white/10">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
              <p className="text-xs text-zinc-400 leading-relaxed">
                {en
                  ? "Precise, consistent measurements are what makes your progress comparison and analysis meaningful over time."
                  : "Des mesures précises et régulières sont ce qui rend votre comparaison et votre analyse de progression fiables dans le temps."}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="mt-5 w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform"
            data-testid="measurement-guide-got-it"
          >
            {en ? "Got it, let's measure!" : 'J\'ai compris, à mes mensurations !'}
          </button>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
  );
};

export default MeasurementGuideModal;
